import styled from "styled-components";
import { motion } from "framer-motion";
import { ShieldCheck, ChevronDown } from "lucide-react";

/* ================= SECTION ================= */

const Section = styled.section`
  position: relative;
  padding: 140px 20px;
  overflow: hidden;

  background: radial-gradient(
      800px at 18% 0%,
      rgba(245, 158, 11, 0.12),
      transparent 55%
    ),
    radial-gradient(
      760px at 82% 24%,
      rgba(56, 189, 248, 0.1),
      transparent 55%
    ),
    ${({ theme }) => theme.colors.bg};

  &:after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.6),
      transparent 40%,
      rgba(0, 0, 0, 0.6)
    );
    pointer-events: none;
  }
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 900px;
  margin: auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

/* ================= ICON ================= */

const IconWrap = styled(motion.div)`
  width: 72px;
  height: 72px;
  border-radius: 24px;
  display: grid;
  place-items: center;
  margin: 0 auto 36px;
  color: #fff;

  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  box-shadow: 0 0 60px rgba(56, 189, 248, 0.35);
`;

/* ================= TEXT ================= */

const Heading = styled(motion.h2)`
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.15;
  margin-bottom: 22px;
`;

const Text = styled(motion.p)`
  font-size: 18px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= SCROLL ARROW ================= */

const ScrollHint = styled(motion.div)`
  margin-top: 52px;
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
`;

/* ================= ANIMATIONS ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const bounceDown = {
  animate: {
    y: [0, 12, 0],
    opacity: [0.4, 1, 0.4],
  },
  transition: {
    duration: 1.8,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

/* ================= COMPONENT ================= */

export default function BridgeSection() {
  return (
    <Section>
      <Container>
        <IconWrap
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <ShieldCheck size={34} />
        </IconWrap>

        <Heading
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          The good news?
          <br />
          You do not need more motivation.
          <br />
          You need a system that removes confusion.
        </Heading>

        <Text
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          People do not stay stuck because they are incapable. They stay stuck
          because nobody turns the big goal into a realistic daily path.
          <br />
          <strong>
            `skilll.dev` gives that path: clarity first, then day-by-day
            execution.
          </strong>
        </Text>

        {/* ⬇ Scroll to next section */}
        <ScrollHint
          {...bounceDown}
          onClick={() =>
            document.getElementById("solution-section")?.scrollIntoView({
              behavior: "smooth",
            })
          }
        >
          <ChevronDown size={34} />
        </ScrollHint>
      </Container>
    </Section>
  );
}
